import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { BlogDetailSkeleton } from '@/components/blog/BlogDetailSkeleton';
import { NotFound } from '@/components/NotFound';
import { getSiteUrl } from '@/lib/siteUrl';

export const Route = createFileRoute('/blog/$slug')({
  component: BlogDetailPage,
});

function BlogDetailPage() {
  const { slug } = Route.useParams();
  const siteUrl = getSiteUrl();

  const {
    data: post,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const res = await fetch(`/api/blog/posts/${slug}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('NOT_FOUND');
        throw new Error('Failed to fetch');
      }
      const json = await res.json();
      return json.data;
    },
    retry: (_, err) => !(err instanceof Error && err.message === 'NOT_FOUND'),
  });

  if (isLoading) {
    return (
      <article>
        <Link to="/blog" className="inline-block text-gray-400 hover:text-white mb-8">
          ← Back to Blog
        </Link>
        <BlogDetailSkeleton />
      </article>
    );
  }

  if (error || !post) {
    const isNotFound = error instanceof Error && error.message === 'NOT_FOUND';
    return (
      <article>
        <Link to="/blog" className="inline-block text-gray-400 hover:text-white mb-8">
          ← Back to Blog
        </Link>
        <NotFound
          title={isNotFound ? 'Post not found' : 'Could not load post'}
          message={
            isNotFound
              ? "The post you're looking for doesn't exist or has been removed."
              : 'Something went wrong while loading this post. Please try again.'
          }
        />
      </article>
    );
  }

  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const dateISO = post.publishedAt ?? undefined;
  const updatedISO = post.updatedAt ?? post.publishedAt ?? undefined;

  const postUrl = `${siteUrl}/blog/${post.slug}`;
  const ogImage = post.featuredImageUrl ?? `${siteUrl}/logo.webp`;
  // Use admin-provided meta fields if available, otherwise fallback to excerpt/title
  const metaTitle = post.metaTitle || `${post.title} | AITOS Blog`;
  const metaDescription = post.metaDescription || post.excerpt || post.title;
  const metaKeywords = post.metaKeywords || undefined;
  const featuredImageAlt = post.featuredImageAlt || post.title;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: metaDescription,
    image: [ogImage],
    url: postUrl,
    datePublished: dateISO,
    dateModified: updatedISO,
    inLanguage: 'en',
    author: {
      '@type': 'Person',
      name: post.author?.name ?? 'AITOS',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AITOS',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.webp`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
    ...(Array.isArray(post.keywords) && post.keywords.length > 0
      ? { keywords: post.keywords.join(', ') }
      : metaKeywords
        ? { keywords: metaKeywords }
        : {}),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: postUrl },
    ],
  };

  return (
    <>
      <Helmet>
        {/* Primary */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        <link rel="canonical" href={postUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="AITOS Blog" />
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={featuredImageAlt} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:locale" content="en_US" />
        {dateISO && <meta property="article:published_time" content={dateISO} />}
        {updatedISO && <meta property="article:modified_time" content={updatedISO} />}
        <meta property="article:author" content={post.author?.name ?? 'AITOS'} />
        {Array.isArray(post.tags) &&
          post.tags.map((tag: string) => <meta key={tag} property="article:tag" content={tag} />)}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={featuredImageAlt} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <article itemScope itemType="https://schema.org/BlogPosting">
        <Link to="/blog" className="inline-block text-gray-400 hover:text-white mb-8">
          ← Back to Blog
        </Link>

        <header className="mb-8">
          {post.featuredImageUrl && (
            <div className="aspect-video rounded-xl overflow-hidden mb-6">
              <img
                src={post.featuredImageUrl}
                alt={featuredImageAlt}
                className="w-full h-full object-cover"
                itemProp="image"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          )}

          {/* Breadcrumb visible */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1 text-sm text-gray-500 mb-4"
          >
            <Link to="/" className="hover:text-gray-300">
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-gray-300">
              Blog
            </Link>
            <span>/</span>
            <span className="text-gray-400 line-clamp-1">{post.title}</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-bold mb-4" itemProp="headline">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="text-lg text-gray-400 mb-4 leading-relaxed" itemProp="description">
              {post.excerpt}
            </p>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            {Array.isArray(post.tags) &&
              post.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-sm rounded-full bg-purple-500/20 text-purple-400"
                >
                  #{tag}
                </span>
              ))}
          </div>

          <div className="flex items-center gap-3 text-gray-400 text-sm">
            <span
              itemProp="author"
              itemScope
              itemType="https://schema.org/Person"
              className="font-medium text-gray-300"
            >
              <span itemProp="name">{post.author?.name ?? 'AITOS'}</span>
            </span>
            {date && (
              <>
                <span>•</span>
                <time dateTime={dateISO} itemProp="datePublished">
                  {date}
                </time>
              </>
            )}
            {updatedISO && updatedISO !== dateISO && (
              <meta itemProp="dateModified" content={updatedISO} />
            )}
          </div>
        </header>

        <div
          className="prose prose-invert prose-lg max-w-none leading-relaxed [&_a]:text-purple-400 [&_a]:hover:text-purple-300 [&_pre]:bg-gray-900 [&_pre]:p-4 [&_pre]:rounded-lg [&_code]:bg-gray-800 [&_code]:px-1 [&_code]:rounded [&_img]:rounded-lg [&_img]:my-8 [&_img]:block lg:[&_img]:w-[60%] lg:[&_img]:mx-auto [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:list-inside [&_ol]:list-inside [&_ul]:pl-4 [&_ol]:pl-4 [&_u]:underline [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:mt-10 [&_h1]:mb-6 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3"
          itemProp="articleBody"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </>
  );
}
